import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Plus } from "lucide-react";
import { BrokerMark, MatchRing, Tag } from "@/components/shared/Primitives";
import { useCompare } from "@/context/CompareContext";
import { CRITERIA } from "@/data/criteria";

const Cell = ({ label, children }) => (
  <div>
    <p className="eyebrow mb-1.5">{label}</p>
    <div className="text-[15px] text-[var(--ink-2)] leading-[1.55]">{children}</div>
  </div>
);

export const BrokerResult = ({ result, index }) => {
  const { broker: b, score, entity, breakdown, notes = [] } = result;
  const { toggle, has } = useCompare();
  const on = has(b.slug);
  const strong = breakdown.filter((x) => x.fit >= 0.7).map((x) => CRITERIA.find((c) => c.id === x.id).short);
  const weak = breakdown.filter((x) => x.fit < 0.4).map((x) => CRITERIA.find((c) => c.id === x.id).short);

  return (
    <motion.article layout initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, delay: index * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
      className="card p-6 sm:p-8" data-testid={`result-${b.slug}`}>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <BrokerMark broker={b} size={52} />
          <div>
            <div className="flex items-center gap-3">
              <span className="eyebrow">Match 0{index + 1}</span>
              <span className="font-mono text-[11px] text-[var(--ink-4)]">{entity.regulator} · {entity.jurisdiction}</span>
            </div>
            <h3 className="mt-1.5 t-h4">{b.name}</h3>
            <p className="mt-1 text-sm text-[var(--ink-3)]">{entity.name} · Ref {entity.ref}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {strong.map((s) => <Tag key={s} tone="blue">{s}</Tag>)}
              {weak.map((s) => <Tag key={s}>Weak: {s}</Tag>)}
              {notes.map((n) => <Tag key={n}>{n}</Tag>)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-5 lg:flex-col lg:items-end lg:gap-2">
          <MatchRing value={score} size={72} stroke={5} />
          <p className="font-mono text-[11px] text-[var(--ink-4)]">Match score · rules-based</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-6 border-t border-[var(--line)] pt-6 lg:grid-cols-4">
        <Cell label="Trading benefits">{b.highlight}</Cell>
        <Cell label="Costs">{b.spreadModel} · EUR/USD {b.eurUsdSpread.toFixed(1)} pips{b.commission ? ` + $${b.commission.toFixed(2)}/lot` : ", no commission"}<br /><span className="text-[var(--ink-3)]">Swap-free: {b.swapFree ? "yes" : "no"}</span></Cell>
        <Cell label="Funding">{b.funding.slice(0, 3).join(", ")}{b.funding.length > 3 ? ` +${b.funding.length - 3}` : ""}<br /><span className="text-[var(--ink-3)]">Withdrawal: {b.withdrawalTime}</span></Cell>
        <Cell label="Leverage & platforms">{entity.leverage} max<br /><span className="text-[var(--ink-3)]">{b.platforms.join(" · ")}</span></Cell>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-5">
        <div className="flex items-center gap-2">
          <button onClick={() => toggle(b.slug, b.short)} data-on={on} data-testid={`result-compare-${b.slug}`} className="pill h-9 text-[13px]">
            {on ? <Check size={14} /> : <Plus size={14} />}{on ? "In compare" : "Compare details"}
          </button>
          <Link to={`/brokers/${b.slug}`} className="btn-ghost h-9 px-4 text-[13px]" data-testid={`result-profile-${b.slug}`}>Full profile</Link>
        </div>
        <a href={b.referral} target="_blank" rel="noreferrer sponsored" className="btn-primary h-10 px-5 text-[13px]" data-testid={`result-visit-${b.slug}`}>Visit broker <ArrowUpRight size={15} /></a>
      </div>
      <p className="mt-3 font-mono text-[10px] text-[var(--ink-4)]">External referral link to {entity.name}'s registration page. Eligibility: {entity.regions.length > 6 ? `${entity.regions.slice(0, 4).join(", ")} and ${entity.regions.length - 4} more` : entity.regions.join(", ")}.</p>
    </motion.article>
  );
};
